/**
 * Shared Database Enums
 *
 * Single source of truth for all database enums shared between frontend and backend.
 * These match the Prisma schema enums exactly to ensure type consistency.
 *
 * Usage:
 *   - Contracts import from here instead of relying on generated files
 *   - Server uses these for API layer (generated Prisma types for DB layer)
 *   - Frontend uses these directly from shared-types
 */

import { z } from "zod";

// ============================================================================
// Event Types
// ============================================================================

/**
 * Zod schema for event type validation.
 * Accepts any non-empty string to support dynamic, config-driven event types.
 */
export const EventTypeSchema = z.string().min(1);

/** Event type - any non-empty string ID (e.g., 'CHECK_UP', 'FOLLOW_UP') */
export type EventType = string;

/** Well-known event type constants. New types are defined in AppConfig.eventTypes. */
export const EVENT_TYPE = {
	CHECK_UP: "CHECK_UP",
	FOLLOW_UP: "FOLLOW_UP",
} as const;

/** Type guard for EventType - accepts any non-empty string */
export function isEventType(value: unknown): value is EventType {
	return typeof value === "string" && value.length > 0;
}

/**
 * Convert a display name to an event type ID (UPPER_SNAKE_CASE).
 * e.g., "Check-up" -> "CHECK_UP", "Follow Up" -> "FOLLOW_UP"
 */
export function nameToEventTypeId(name: string): string {
	return name
		.trim()
		.replace(/[^a-zA-Z0-9\s_-]/g, "")
		.replace(/[\s-]+/g, "_")
		.toUpperCase();
}

// ============================================================================
// Event Status
// ============================================================================

/** Zod schema for event status validation */
export const EventStatusSchema = z
	.string()
	.min(1)
	.max(64)
	.regex(/^[A-Z][A-Z0-9_]*$/);

/** Event status union type */
export type EventStatus = z.infer<typeof EventStatusSchema>;

/** Event status values as const object */
export const EVENT_STATUS = {
	CONFIRMED: "CONFIRMED",
	COMPLETED: "COMPLETED",
	NO_SHOW: "NO_SHOW",
} as const satisfies Record<string, EventStatus>;

/** Type guard for EventStatus */
export function isEventStatus(value: unknown): value is EventStatus {
	return EventStatusSchema.safeParse(value).success;
}

// ============================================================================
// Message Direction
// ============================================================================

/** Zod schema for message direction validation */
export const MessageDirectionSchema = z.enum(["INBOUND", "OUTBOUND"]);

/** Message direction union type */
export type MessageDirection = z.infer<typeof MessageDirectionSchema>;

/** Message direction values as const object */
export const MESSAGE_DIRECTION = {
	INBOUND: "INBOUND",
	OUTBOUND: "OUTBOUND",
} as const satisfies Record<string, MessageDirection>;

/** Type guard for MessageDirection */
export function isMessageDirection(value: unknown): value is MessageDirection {
	return MessageDirectionSchema.safeParse(value).success;
}

// ============================================================================
// User Role (Better Auth admin plugin convention)
// ============================================================================
//
// Platform-level role on the User table. Per Better Auth's admin plugin:
//   - "user" = default for everyone
//   - "superuser" = cross-org platform admin (passed via adminRoles config)
//
// Per-org roles ("admin" / "secretary" / "guest" / "owner") live on
// Member.role and are managed by Better Auth's organization plugin.

/** Zod schema for user (platform) role validation */
export const UserRoleSchema = z.enum(["superuser", "user"]);

/** User (platform) role union type */
export type UserRole = z.infer<typeof UserRoleSchema>;

/** User (platform) role values */
export const USER_ROLE: {
	readonly SUPERUSER: "superuser";
	readonly USER: "user";
} = {
	SUPERUSER: "superuser",
	USER: "user",
} as const;

/** Type guard for UserRole */
export function isUserRole(value: unknown): value is UserRole {
	return UserRoleSchema.safeParse(value).success;
}

// ============================================================================
// Member Role (Better Auth organization plugin convention)
// ============================================================================

/** Zod schema for per-org member role validation */
export const MemberRoleSchema = z.enum([
	"owner",
	"admin",
	"secretary",
	"guest",
]);

/** Per-org member role union type */
export type MemberRole = z.infer<typeof MemberRoleSchema>;

/** Per-org member role values */
export const MEMBER_ROLE: {
	readonly OWNER: "owner";
	readonly ADMIN: "admin";
	readonly SECRETARY: "secretary";
	readonly GUEST: "guest";
} = {
	OWNER: "owner",
	ADMIN: "admin",
	SECRETARY: "secretary",
	GUEST: "guest",
} as const;

/** Type guard for MemberRole */
export function isMemberRole(value: unknown): value is MemberRole {
	return MemberRoleSchema.safeParse(value).success;
}

// ============================================================================
// Domain Event Type
// ============================================================================

/**
 * Zod schema for domain event type validation.
 * All domain events that can be published/subscribed to in the system.
 */
export const DomainEventTypeSchema = z.enum([
	// Event lifecycle
	"EVENT_CREATED",
	"EVENT_UPDATED",
	"EVENT_CANCELLED",
	"EVENT_REINSTATED",
	"EVENT_RESCHEDULED",
	"EVENT_SCHEDULED",
	"EVENT_COMPLETED",
	"EVENT_NO_SHOW",
	// Conversation events
	"CONVERSATION_NEW_MESSAGE",
	"CONVERSATION_CLEARED",
	"CONVERSATION_TYPING",
	"MESSAGE_RECORDED",
	"CONTACT_PHONE_LINKED",
	// User events
	"USER_DOCUMENT_UPDATED",
	"USER_FAVORITE_UPDATED",
	"USER_ACTIVE_UPDATED",
	"USER_BLOCK_UPDATED",
	"USER_UPDATED",
	"USER_SIGNED_IN",
	"USER_SUSPENDED",
	// Password events
	"PASSWORD_RESET_REQUESTED",
	"PASSWORD_RESET_COMPLETED",
	"PASSWORD_CHANGED",
	// Config events
	"VACATION_PERIOD_ADDED",
	"VACATION_PERIOD_UPDATED",
	"VACATION_PERIOD_DELETED",
	// System events
	"SNAPSHOT",
	"METRICS_UPDATED",
]);

/** Domain event type union type */
export type DomainEventType = z.infer<typeof DomainEventTypeSchema>;

/** Domain event type values as const object */
export const DOMAIN_EVENT_TYPE = {
	// Event lifecycle
	EVENT_CREATED: "EVENT_CREATED",
	EVENT_UPDATED: "EVENT_UPDATED",
	EVENT_CANCELLED: "EVENT_CANCELLED",
	EVENT_REINSTATED: "EVENT_REINSTATED",
	EVENT_RESCHEDULED: "EVENT_RESCHEDULED",
	EVENT_SCHEDULED: "EVENT_SCHEDULED",
	EVENT_COMPLETED: "EVENT_COMPLETED",
	EVENT_NO_SHOW: "EVENT_NO_SHOW",
	// Conversation events
	CONVERSATION_NEW_MESSAGE: "CONVERSATION_NEW_MESSAGE",
	CONVERSATION_CLEARED: "CONVERSATION_CLEARED",
	CONVERSATION_TYPING: "CONVERSATION_TYPING",
	MESSAGE_RECORDED: "MESSAGE_RECORDED",
	CONTACT_PHONE_LINKED: "CONTACT_PHONE_LINKED",
	// User events
	USER_DOCUMENT_UPDATED: "USER_DOCUMENT_UPDATED",
	USER_FAVORITE_UPDATED: "USER_FAVORITE_UPDATED",
	USER_ACTIVE_UPDATED: "USER_ACTIVE_UPDATED",
	USER_BLOCK_UPDATED: "USER_BLOCK_UPDATED",
	USER_UPDATED: "USER_UPDATED",
	USER_SIGNED_IN: "USER_SIGNED_IN",
	USER_SUSPENDED: "USER_SUSPENDED",
	// Password events
	PASSWORD_RESET_REQUESTED: "PASSWORD_RESET_REQUESTED",
	PASSWORD_RESET_COMPLETED: "PASSWORD_RESET_COMPLETED",
	PASSWORD_CHANGED: "PASSWORD_CHANGED",
	// Config events
	VACATION_PERIOD_ADDED: "VACATION_PERIOD_ADDED",
	VACATION_PERIOD_UPDATED: "VACATION_PERIOD_UPDATED",
	VACATION_PERIOD_DELETED: "VACATION_PERIOD_DELETED",
	// System events
	SNAPSHOT: "SNAPSHOT",
	METRICS_UPDATED: "METRICS_UPDATED",
} as const satisfies Record<string, DomainEventType>;

/** Type guard for DomainEventType */
export function isDomainEventType(value: unknown): value is DomainEventType {
	return DomainEventTypeSchema.safeParse(value).success;
}

// ============================================================================
// Inbound Message Status
// ============================================================================

/** Zod schema for inbound message status validation */
export const InboundMessageStatusSchema = z.enum([
	"PENDING",
	"PROCESSING",
	"DONE",
	"FAILED",
]);

/** Inbound message status union type */
export type InboundMessageStatus = z.infer<typeof InboundMessageStatusSchema>;

/** Inbound message status values as const object */
export const INBOUND_MESSAGE_STATUS = {
	PENDING: "PENDING",
	PROCESSING: "PROCESSING",
	DONE: "DONE",
	FAILED: "FAILED",
} as const satisfies Record<string, InboundMessageStatus>;

/** Type guard for InboundMessageStatus */
export function isInboundMessageStatus(
	value: unknown
): value is InboundMessageStatus {
	return InboundMessageStatusSchema.safeParse(value).success;
}

// ============================================================================
// Notification Source
// ============================================================================

/**
 * Notification source identifies where an operation originated from.
 * Used for subscription-based filtering of toasts and notifications.
 *
 * - local: User-initiated from admin dashboard (Toast only, no notification)
 * - agent: AI agent-initiated via tool calls (Both toast and notification)
 * - external_user: External user message via WhatsApp (Both toast and notification)
 * - system_agent: System agent conversation (Neither toast nor notification)
 */
export const NotificationSourceSchema = z.enum([
	"local",
	"agent",
	"external_user",
	"system_agent",
]);

/** Notification source union type */
export type NotificationSource = z.infer<typeof NotificationSourceSchema>;

/** Notification source values as const object */
export const NOTIFICATION_SOURCE = {
	LOCAL: "local",
	AGENT: "agent",
	EXTERNAL_USER: "external_user",
	SYSTEM_AGENT: "system_agent",
} as const satisfies Record<string, NotificationSource>;

/** Type guard for NotificationSource */
export function isNotificationSource(
	value: unknown
): value is NotificationSource {
	return NotificationSourceSchema.safeParse(value).success;
}

// ============================================================================
// Message Role
// ============================================================================

/** Zod schema for message role validation */
export const MessageRoleSchema = z.enum([
	"user",
	"assistant",
	"secretary",
	"admin",
	"tool",
	"system",
]);

/** Message role union type */
export type MessageRole = z.infer<typeof MessageRoleSchema>;

/** Message role values as const object */
export const MESSAGE_ROLE: {
	readonly USER: "user";
	readonly ASSISTANT: "assistant";
	readonly SECRETARY: "secretary";
	readonly ADMIN: "admin";
	readonly TOOL: "tool";
	readonly SYSTEM: "system";
} = {
	USER: "user",
	ASSISTANT: "assistant",
	SECRETARY: "secretary",
	ADMIN: "admin",
	TOOL: "tool",
	SYSTEM: "system",
} as const;

/** Type guard for MessageRole */
export function isMessageRole(value: unknown): value is MessageRole {
	return MessageRoleSchema.safeParse(value).success;
}
